import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  GetRouterClientIdentityAccountResponse,
  ListRouterClientIdentityAccountsResponse,
  ResetRouterClientIdentitySessionResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import {
  type RouterClientIdentityFilters,
  getRouterClientIdentityAccount,
  listRouterClientIdentityAccounts,
  resetRouterClientIdentitySession,
} from './routerClientIdentities.ts';

const ROUTER_CLIENT_IDENTITIES_KEY = ['router-client-identities'] as const;

export function useRouterClientIdentityAccounts(
  filters: RouterClientIdentityFilters,
): UseQueryResult<ListRouterClientIdentityAccountsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ROUTER_CLIENT_IDENTITIES_KEY, 'accounts', filters] as const,
    queryFn: ({ signal }) => listRouterClientIdentityAccounts(filters, fetchImpl, signal),
  });
}

export function useRouterClientIdentityAccount(
  id: string | null,
): UseQueryResult<GetRouterClientIdentityAccountResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ROUTER_CLIENT_IDENTITIES_KEY, 'account', id] as const,
    enabled: id !== null,
    queryFn: ({ signal }) => getRouterClientIdentityAccount(id ?? '', fetchImpl, signal),
  });
}

export function useResetRouterClientIdentitySession(): UseMutationResult<
  ResetRouterClientIdentitySessionResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => resetRouterClientIdentitySession(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTER_CLIENT_IDENTITIES_KEY });
    },
  });
}
